import axios from 'axios'
import { Message } from 'element-ui'
import { getToken, setToken, removeToken } from '@/utils/storage'

const service = axios.create({
  baseURL: '/api/v1',
  timeout: 120000
})

// ===== token 自动续期（滑动过期）=====
// token 有效期 30 分钟；请求前检查 JWT exp，剩余不足 5 分钟时先调用 /auth/refresh 换新 token
// - 并发请求通过共享 refreshPromise 去重，只发一次刷新
// - 刷新走原生 axios（绕开本拦截器，避免递归）
// - 刷新失败（token 已完全过期）不阻断：请求会收到 401，由响应拦截器统一跳登录页
// - 后台轮询类请求（RENEW_EXEMPT_PATHS）不参与续期：页面挂着无人操作时 token 自然过期，
//   下一个 401 会把页面带回登录页；只有用户真实操作才续命
const RENEW_THRESHOLD_MS = 5 * 60 * 1000
const RENEW_EXEMPT_PATHS = ['/notifications/unread-count']
let refreshPromise = null

function parseJwtExpMs(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload.exp ? payload.exp * 1000 : 0
  } catch (e) {
    return 0
  }
}

function ensureFreshToken() {
  const token = getToken()
  if (!token) return Promise.resolve()
  const expMs = parseJwtExpMs(token)
  if (!expMs || expMs - Date.now() > RENEW_THRESHOLD_MS) return Promise.resolve()
  if (!refreshPromise) {
    refreshPromise = axios.post('/api/v1/auth/refresh', null, {
      headers: { Authorization: 'Bearer ' + token },
      timeout: 10000
    }).then(res => {
      const newToken = res.data && res.data.data && res.data.data.token
      if (newToken) setToken(newToken)
      refreshPromise = null
    }).catch(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// 后台轮询请求不触发 token 续期
function isRenewExempt(config) {
  const url = (config && config.url) || ''
  return RENEW_EXEMPT_PATHS.some(path => url.indexOf(path) !== -1)
}

// 将 snake_case 转为 camelCase
function toCamelCase(obj) {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase)
  }
  if (typeof obj === 'object' && !(obj instanceof File) && !(obj instanceof FormData)) {
    const result = {}
    for (const key of Object.keys(obj)) {
      const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
      result[camelKey] = toCamelCase(obj[key])
    }
    return result
  }
  return obj
}

// 将 camelCase 转为 snake_case
function toSnakeCase(obj) {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) {
    return obj.map(toSnakeCase)
  }
  if (typeof obj === 'object' && !(obj instanceof File) && !(obj instanceof FormData)) {
    const result = {}
    for (const key of Object.keys(obj)) {
      const snakeKey = key.replace(/([A-Z])/g, (_, c) => '_' + c.toLowerCase())
      result[snakeKey] = toSnakeCase(obj[key])
    }
    return result
  }
  return obj
}

service.interceptors.request.use(
  async config => {
    // 后台轮询请求不参与续期，token 过期后直接 401 → 跳登录页，避免页面挂着永不掉线
    if (!isRenewExempt(config)) {
      await ensureFreshToken()
    }
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }
    // 统一把请求参数/数据从 camelCase 转为 snake_case（后端内部统一用 snake_case）
    if (config.params) {
      config.params = toSnakeCase(config.params)
    }
    if (config.data && !(config.data instanceof FormData)) {
      config.data = toSnakeCase(config.data)
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  response => {
    // 二进制响应（如 Excel 导出）直接返回，不需要 JSON 转换
    if (
      response.data instanceof Blob ||
      response.config.responseType === 'blob' ||
      response.config.responseType === 'arraybuffer'
    ) {
      // 挂上响应头供调用方读取（如批量下载的 X-Doc-Total / X-Doc-Skipped），
      // 不改变返回值类型，旧调用方无感知
      try { response.data.__headers = response.headers } catch (e) {}
      return response.data
    }
    const res = response.data
    // 后端返回格式: {code: 200, message: "success", data: {...}}
    // code=200 或 code=0 或无 code 都按成功处理，其余视为业务错误
    const isBusinessError =
      res && typeof res === 'object' &&
      res.code !== undefined &&
      res.code !== 200 &&
      res.code !== 0
    if (isBusinessError) {
      const msg = res.message || '请求失败'
      try { Message.error(msg) } catch (e) {}
      const err = new Error(msg)
      err.code = res.code
      err.__isBusinessError = true // 业务错误：已经 toast 提示给用户，避免未 catch 时触发 Vue CLI dev overlay
      return Promise.reject(err)
    }
    // 将 data 中的 snake_case 转为 camelCase
    if (res && typeof res === 'object' && res.data !== undefined) {
      res.data = toCamelCase(res.data)
    }
    return res
  },
  async error => {
    if (error.response && error.response.status === 401) {
      removeToken()
      // 跳登录时携带当前页面路径（如邮件里的工资条链接），登录成功后可直接跳回，
      // 避免从邮件进入但本地 token 已过期时，登录后只落到默认工作台
      try {
        const path = window.location.pathname + window.location.search + window.location.hash
        if (!path.startsWith('/login')) {
          window.location.href = '/login?redirect=' + encodeURIComponent(path)
        } else {
          window.location.href = '/login'
        }
      } catch (e) {
        window.location.href = '/login'
      }
    }
    let message = '网络错误'
    if (error && error.response && error.response.data) {
      const status = error.response.status
      const errData = error.response.data
      if (typeof Blob !== 'undefined' && errData instanceof Blob) {
        // blob 响应出错（如打包下载超限 400）：错误 JSON 被包成 Blob，先解出 message
        // 兼容两种格式：FastAPI HTTPException {"detail": "..."} / 业务 error_response {"message": "..."}
        try {
          const txt = await errData.text()
          const parsed = JSON.parse(txt)
          message = (parsed && (parsed.detail || parsed.message)) || ('请求失败（HTTP ' + status + '）')
        } catch (e) {
          message = '请求失败（HTTP ' + status + '）'
        }
      } else if (typeof errData === 'object' && errData.message && typeof errData.message === 'string') {
        message = errData.message
      } else if (status === 500) {
        // 后端兜底失败：若返回裸 HTML 或 traceback，则给一个可读提示
        if (typeof errData === 'string') {
          if (/<html/i.test(errData) || /<body/i.test(errData)) {
            message = '服务器繁忙，请稍后重试（HTTP 500）'
          } else if (errData.length > 200) {
            message = errData.split(/\r?\n/)[0].slice(0, 200)
          } else {
            message = errData
          }
        } else if (typeof errData === 'object' && Array.isArray(errData.detail)) {
          const parts = errData.detail.map(d => {
            const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : (d.loc || '')
            const m = d.msg || '参数错误'
            return field ? `${field}: ${m}` : `${m}`
          })
          message = parts.length === 1 ? parts[0] : `参数错误：${parts.join('；')}`
        } else if (typeof errData === 'object' && errData.detail && typeof errData.detail === 'string') {
          message = errData.detail
        } else {
          message = '服务器繁忙，请稍后重试（HTTP 500）'
        }
      } else if (typeof errData === 'object' && Array.isArray(errData.detail)) {
        const parts = errData.detail.map(d => {
          const field = Array.isArray(d.loc) ? d.loc.slice(-1)[0] : (d.loc || '')
          const m = d.msg || '参数错误'
          return field ? `${field}: ${m}` : `${m}`
        })
        message = parts.length === 1 ? parts[0] : `参数错误：${parts.join('；')}`
      } else if (typeof errData === 'object' && errData.detail && typeof errData.detail === 'string') {
        message = errData.detail
      } else if (error.message) {
        message = error.message
      }
    } else if (error && error.message) {
      message = error.message
    }
    // 401 已强制跳登录，无需再 toast
    const is401 = !!(error && error.response && error.response.status === 401)
    // 首次登录/强制改密期间（mustChangePassword=true），后端会拦截白名单外的所有接口返回 403。
    // 该提示已由全局"首次登录，请修改密码"弹窗展示，这里静默处理，避免弹窗 + 多条 toast 重复提示
    const isForcedPwdBlock = !is401 &&
      error.response && error.response.status === 403 &&
      typeof message === 'string' &&
      message.indexOf('请先修改初始密码后再使用系统') !== -1
    if (!is401 && !isForcedPwdBlock) {
      try { Message.error(message) } catch (e) {}
    }
    const errObj = new Error(message)
    errObj.__isBusinessError = true // 已通过 toast 展示过，避免未 catch 时触发 dev overlay 红色弹窗
    return Promise.reject(errObj)
  }
)

/**
 * 全局"已处理业务级错误"的 Promise 未捕获拦截：
 *  - axios interceptor 已通过 Message.error 给用户弹过 toast
 *  - 但如果调用方没写 .catch，会产生一个 unhandledrejection，Vue CLI dev overlay 会渲染成红色报错遮罩
 *  - 这里统一把标记了 __isBusinessError 的 rejection 吞掉，避免开发环境噪声和用户困惑
 */
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', function (event) {
    const reason = (event && event.reason) || null
    if (reason && reason.__isBusinessError === true) {
      try { event.preventDefault() } catch (e) {}
      return false
    }
  })
}

export default service